import axios from '@/axiosInstance.ts'
import {PaginationParams} from '@/types.ts'
import {ConversationEntityType, MemberReference} from '@/features/conversations/types'
import {z} from 'zod'

/**
 * Get conversations for a patient.
 * Pass the literal string 'null' as patientId to fetch only internal conversations
 * (patient_id IS NULL server-side); axios would drop a real null param.
 */
export const INTERNAL_CONVERSATIONS_PATIENT_ID_PARAM = 'null'

export interface HttpGetConversationsOptions {
    params: PaginationParams & {
        patientId?: string
        search?: string
        entityType?: ConversationEntityType
    }
}

export const httpGetConversations = ({params}: HttpGetConversationsOptions) => axios.get('conversations', {params})

/**
 * Create conversation payload
 */
export const CreateConversationPayload = z.object({
    // Omitted (or null) when creating an internal conversation without a patient
    patientId: z.string().min(1).nullable().optional(),
    members: z.array(MemberReference),
    title: z.string(),
    message: z.string(),
    isUrgent: z.boolean(),
    entityType: ConversationEntityType.optional(),
    entityId: z.string().optional()
})
export type CreateConversationPayload = z.infer<typeof CreateConversationPayload>

/**
 * Create a new conversation
 */
export const httpCreateConversation = (payload: CreateConversationPayload) => axios.post('conversations', payload)

/**
 * Get messages for a conversation
 */
export interface HttpGetConversationMessagesOptions {
    params: PaginationParams
}

export const httpGetConversationMessages = (conversationId: string, options?: HttpGetConversationMessagesOptions) =>
    axios.get(`conversations/${conversationId}/messages`, options)

/**
 * Send message payload
 */
export const SendMessagePayload = z.object({
    conversationId: z.string().min(1),
    message: z.string().min(1),
    isUrgent: z.boolean()
})
export type SendMessagePayload = z.infer<typeof SendMessagePayload>

export const httpSendConversationMessage = (payload: SendMessagePayload) =>
    axios.post(`conversations/${payload.conversationId}/messages`, payload)

/**
 * Mark conversation messages as read
 */
export const httpMarkConversationMessagesAsRead = (conversationId: string) =>
    axios.post(`conversations/${conversationId}/mark-as-read`)

/**
 * Delete a conversation
 */
export const httpDeleteConversation = (conversationId: string) => axios.delete(`conversations/${conversationId}`)

/**
 * Update conversation members payload
 */
export const UpdateConversationMembersPayload = z.object({
    members: z.array(MemberReference)
})
export type UpdateConversationMembersPayload = z.infer<typeof UpdateConversationMembersPayload>

/**
 * Update conversation members
 */
export const httpUpdateConversationMembers = (conversationId: string, payload: UpdateConversationMembersPayload) =>
    axios.patch(`conversations/${conversationId}`, payload)
